/**
 * Done. CPD — Dashboard Hour Tracker (Stitch v2 / screen 7)
 * Circular gauge for annual CPD hours.
 */

import type { CapStatus } from '@/lib/types';
import { formatHours } from '@/lib/utils';

interface HourTrackerProps {
  capStatus: CapStatus;
}

export function HourTracker({ capStatus }: HourTrackerProps): React.ReactElement {
  const { annualHoursLogged, annualHourCap } = capStatus;
  const pctRaw = annualHourCap > 0 ? (annualHoursLogged / annualHourCap) * 100 : 0;
  const progress = Math.min(100, Math.max(0, pctRaw));
  const pctLabel = Math.round(progress);

  return (
    <div className="bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-surface-container-high)] p-10 rounded-xl stitch-editorial-shadow flex flex-col items-center text-center">
      <h3 className="stitch-font-label text-[13px] text-[var(--stitch-primary)] mb-8 tracking-[0.2em] uppercase">
        Hour Tracker
      </h3>
      <div className="relative w-52 h-52 mb-8">
        <div
          className="stitch-circular-progress w-full h-full rounded-full flex items-center justify-center transition-all duration-1000"
          style={{ ['--progress' as string]: progress } as React.CSSProperties}
        >
          <div className="bg-[var(--stitch-surface-container-lowest)] w-[92%] h-[92%] rounded-full flex flex-col items-center justify-center">
            <span className="stitch-font-display text-5xl text-[var(--stitch-primary)] leading-none">
              {formatHours(annualHoursLogged)}
            </span>
            <span className="stitch-font-label text-[11px] text-[var(--stitch-on-surface-variant)] tracking-wider mt-2 uppercase">
              Completed
            </span>
          </div>
        </div>
      </div>
      <p className="stitch-font-body text-[var(--stitch-on-surface-variant)]">
        {formatHours(annualHoursLogged)} of {annualHourCap} hours completed
      </p>
      <p className="text-xs text-[var(--stitch-outline)] mt-2 italic stitch-font-body">
        {pctLabel}% through your annual requirement
      </p>
    </div>
  );
}
